/**
 * Parse chapter number from filename
 * Supports various formats:
 * - Chapter 42, chapter42, Ch.42, ch 42
 * - Глава 42, глава42, Гл.42, гл 42
 * - 042, 0042 (pure numbers)
 * - 42.5, 42v2, 42-1 (fractional and versions)
 * - 001-050 (ranges -> first number)
 * - 188화, 188话 (Korean/Chinese)
 * - _17_, -17- (number between separators)
 */

const patterns: Array<{ regex: RegExp; groupIndex: number }> = [
  // English patterns
  { regex: /chapter[_\s.-]*(\d+(?:\.\d+)?)/i, groupIndex: 1 },
  { regex: /ch[_\s.-]*(\d+(?:\.\d+)?)/i, groupIndex: 1 },

  // Russian patterns
  { regex: /глава[_\s.-]*(\d+(?:\.\d+)?)/i, groupIndex: 1 },
  { regex: /гл[._\s-]*(\d+(?:\.\d+)?)/i, groupIndex: 1 },

  // Korean/Chinese patterns
  { regex: /(\d+)화/, groupIndex: 1 },
  { regex: /(\d+)话/, groupIndex: 1 },

  // Version patterns (take the main number)
  { regex: /(\d+)v\d+/i, groupIndex: 1 },
  { regex: /(\d+)-\d+(?:\.|$)/, groupIndex: 1 },

  // Number between separators
  { regex: /[_-](\d+)[_-]/, groupIndex: 1 },

  // Range (take first number)
  { regex: /(\d+)-\d+\./, groupIndex: 1 },

  // Pure numbers at start or after separator
  { regex: /^0*(\d+)\./, groupIndex: 1 },
  { regex: /[_-]0*(\d+)\./, groupIndex: 1 },

  // Just digits anywhere in filename (last resort)
  { regex: /(\d+(?:\.\d+)?)/, groupIndex: 1 },
];

export function parseChapterNumber(filename: string): number | null {
  if (!filename) return null;

  // Remove extension
  const nameWithoutExt = filename.replace(/\.[^.]+$/, '');

  for (const { regex, groupIndex } of patterns) {
    const match = nameWithoutExt.match(regex);
    if (match && match[groupIndex]) {
      const num = parseFloat(match[groupIndex]);
      if (!isNaN(num) && num > 0 && num < 10000) {
        return num;
      }
    }
  }

  return null;
}

/**
 * Parse multiple files and return array with parsed chapter numbers
 */
export interface ParsedFile {
  file: File;
  filename: string;
  chapterNo: number | null;
  isValid: boolean;
}

export function parseChapterFiles(files: File[]): ParsedFile[] {
  return files.map((file) => {
    const chapterNo = parseChapterNumber(file.name);
    return {
      file,
      filename: file.name,
      chapterNo,
      isValid: chapterNo !== null,
    };
  });
}

/**
 * Sort files by chapter number (nulls at the end)
 */
export function sortByChapterNumber(files: ParsedFile[]): ParsedFile[] {
  return [...files].sort((a, b) => {
    if (a.chapterNo === null && b.chapterNo === null) return 0;
    if (a.chapterNo === null) return 1;
    if (b.chapterNo === null) return -1;
    return a.chapterNo - b.chapterNo;
  });
}
